import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Build a Google Maps URL for a lead. Prefers the Google Place ID (exact match
 * on the actual listing), falls back to a name+address text search.
 */
export function googleMapsUrl(lead: {
  serp_place_id?: string | null;
  business_name?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
}): string {
  if (lead.serp_place_id) {
    return `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(lead.serp_place_id)}`;
  }
  const parts = [
    lead.business_name,
    lead.address,
    lead.city,
    lead.state,
  ].filter(Boolean);
  const q = encodeURIComponent(parts.join(" "));
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

export type CouncilLead = {
  id: string;
  business_name?: string | null;
  business_type?: string | null;
  city?: string | null;
  state?: string | null;
  website_url?: string | null;
  google_rating?: number | null;
  google_review_count?: number | null;
  notes?: string | null;
  scoring_reasons?: Array<{ impact?: string; label?: string }> | null;
  // Structured discovery (cold-call sheet)
  requested_functionality?: string | null;
  budget?: string | null;
  wants_needs?: string | null;
  avoid?: string | null;
  ready_by?: string | null;
  other_context?: string | null;
  // Decision-maker contact (cold-call sheet)
  owner_name?: string | null;
  owner_email?: string | null;
  owner_cell?: string | null;
};

/**
 * Suggested feature chips shown above the "What are you building?" textarea.
 * Each chip toggles a single bullet line in/out of the auto-managed feature
 * checklist block at the bottom of the prompt. Chip labels are short for the
 * UI; `line` is the longer bullet that ends up in the prompt body.
 */
export const COUNCIL_FEATURE_CHIPS: ReadonlyArray<{
  key: string;
  label: string;
  line: string;
}> = [
  { key: "booking", label: "Online booking", line: "Online booking / appointment scheduling" },
  { key: "quote", label: "Quote calculator", line: "Instant quote / estimate calculator" },
  { key: "service_areas", label: "Service area map", line: "Interactive service area map" },
  { key: "gallery", label: "Photo gallery", line: "Project / photo gallery" },
  { key: "before_after", label: "Before/after gallery", line: "Before / after project gallery" },
  { key: "reviews", label: "Reviews showcase", line: "Customer reviews showcase pulled from Google" },
  { key: "lead_form", label: "Lead capture form", line: "Lead capture form with email + SMS routing" },
  { key: "click_to_call", label: "Click-to-call", line: "Sticky click-to-call buttons on every page" },
  { key: "chat", label: "Live chat", line: "Live chat / contact widget" },
  { key: "service_pages", label: "Service pages w/ SEO", line: "Dedicated service pages optimised for local SEO" },
  { key: "faq", label: "FAQ section", line: "FAQ / objection-handling section" },
  { key: "blog", label: "Blog / content hub", line: "Blog or content hub for SEO" },
  { key: "pricing", label: "Pricing page", line: "Transparent pricing or starting-from page" },
  { key: "team", label: "About / team page", line: "About / team / trust-signals page" },
  { key: "email_capture", label: "Email signup", line: "Email newsletter / promo signup" },
  { key: "mobile_first", label: "Mobile-first design", line: "Mobile-first responsive design" },
  { key: "financing", label: "Financing CTA", line: "Financing or payment-plan callout" },
  { key: "emergency", label: "24/7 emergency", line: "24/7 emergency-service callout" },
];

const FEATURES_BLOCK_START = "=== Feature checklist (managed by chips) ===";
const FEATURES_BLOCK_END = "=== End feature checklist ===";

function renderFeaturesBlock(selectedKeys: ReadonlyArray<string>): string {
  const selected = COUNCIL_FEATURE_CHIPS.filter((c) => selectedKeys.includes(c.key));
  if (selected.length === 0) return "";
  const lines = selected.map((c) => `- ${c.line}`).join("\n");
  return [
    FEATURES_BLOCK_START,
    "Features I want included in this build:",
    lines,
    FEATURES_BLOCK_END,
  ].join("\n");
}

/**
 * Replace (or insert / remove) the auto-managed feature checklist block in
 * `text` to reflect the operator's current chip selection. The block is
 * delimited by sentinel lines so we can find and replace it without touching
 * any other operator edits to the prompt.
 *
 * - Empty `selectedKeys` removes any existing block.
 * - If no block exists yet and chips are selected, the block is appended.
 * - If a block exists, it's replaced in-place so position is preserved.
 */
export function applyFeatureChipsToText(
  text: string,
  selectedKeys: ReadonlyArray<string>,
): string {
  const block = renderFeaturesBlock(selectedKeys);
  const startIdx = text.indexOf(FEATURES_BLOCK_START);
  const endIdx = text.indexOf(FEATURES_BLOCK_END);
  if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
    const before = text.slice(0, startIdx).replace(/\s+$/, "");
    const after = text
      .slice(endIdx + FEATURES_BLOCK_END.length)
      .replace(/^\s+/, "");
    if (block.length === 0) {
      return [before, after].filter(Boolean).join("\n\n");
    }
    return [before, block, after].filter(Boolean).join("\n\n");
  }
  if (block.length === 0) return text;
  return text.trim().length > 0 ? `${text.trim()}\n\n${block}` : block;
}

/**
 * Remove the sentinel marker lines (but keep the bullet content) from a
 * prompt body so the council backend never sees the internal "=== Feature
 * checklist ===" delimiters. Call this right before submitting the prompt.
 */
export function stripFeatureChipsMarkers(text: string): string {
  return text
    .split("\n")
    .filter((line) => {
      const trimmed = line.trim();
      return trimmed !== FEATURES_BLOCK_START && trimmed !== FEATURES_BLOCK_END;
    })
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Inverse of `applyFeatureChipsToText` — read the operator's current chip
 * selection from a piece of prompt text by scanning the auto-managed block.
 * Used to seed chip toggle state from URL prefill / freshly built templates.
 */
export function extractFeatureChipsFromText(text: string): string[] {
  const startIdx = text.indexOf(FEATURES_BLOCK_START);
  const endIdx = text.indexOf(FEATURES_BLOCK_END);
  if (startIdx === -1 || endIdx === -1 || endIdx <= startIdx) return [];
  const block = text.slice(startIdx, endIdx);
  const found: string[] = [];
  for (const c of COUNCIL_FEATURE_CHIPS) {
    if (block.includes(c.line)) found.push(c.key);
  }
  return found;
}

export interface CouncilPrefillOptions {
  /** Latest cold-call notes for this lead — pulled separately because they
   *  live on `lead_calls`, not on the lead row itself. */
  coldCallNotes?: string | null;
  /** Selected feature-chip keys to render as the auto-managed checklist. */
  selectedFeatureChips?: ReadonlyArray<string>;
}

/**
 * Assemble the plain-text prompt for a lead's "Send to Council" action.
 * Mirrors the LEAD BRIEF block the backend injects into agent prompts so the
 * operator can see (and edit) every fact the council will actually receive
 * — including the latest cold-call notes and structured discovery fields.
 *
 * Used both to build the /promptHQ URL and to pre-populate the start form
 * so the textarea is never an empty box when launched from a lead.
 */
export function buildCouncilPrefillText(
  lead: Omit<CouncilLead, "id">,
  options: CouncilPrefillOptions = {},
): string {
  const parts: string[] = [];
  const name = lead.business_name ?? "This business";
  const type = lead.business_type ?? "local services";
  const city = [lead.city, lead.state].filter(Boolean).join(", ");
  parts.push(
    `I want to build a website or digital product for a local ${type} business${city ? ` in ${city}` : ""} called "${name}".`,
  );

  // Business snapshot
  const snap: string[] = [];
  if (lead.website_url) snap.push(`Current website: ${lead.website_url}`);
  if (lead.google_rating != null) {
    const reviews =
      lead.google_review_count != null
        ? ` (${lead.google_review_count} reviews)`
        : "";
    snap.push(`Google rating: ${lead.google_rating}${reviews}`);
  }
  if (snap.length) {
    parts.push(`Business snapshot:\n${snap.map((s) => `- ${s}`).join("\n")}`);
  }

  // Decision-maker (from cold-call sheet)
  const owner: string[] = [];
  if (lead.owner_name?.trim()) owner.push(`Name: ${lead.owner_name.trim()}`);
  if (lead.owner_email?.trim()) owner.push(`Email: ${lead.owner_email.trim()}`);
  if (lead.owner_cell?.trim()) owner.push(`Cell: ${lead.owner_cell.trim()}`);
  if (owner.length) {
    parts.push(`Decision-maker:\n${owner.map((s) => `- ${s}`).join("\n")}`);
  }

  // Structured discovery
  const disc: Array<[string, string | null | undefined]> = [
    ["Requested functionality", lead.requested_functionality],
    ["Wants / needs", lead.wants_needs],
    ["What to avoid", lead.avoid],
    ["Budget signal", lead.budget],
    ["Ready by", lead.ready_by],
    ["Other context", lead.other_context],
  ];
  const discLines = disc
    .filter((entry): entry is [string, string] => Boolean(entry[1] && entry[1].trim()))
    .map(([k, v]) => `- ${k}: ${v.trim()}`);
  if (discLines.length) {
    parts.push(`Discovery (from cold-call sheet):\n${discLines.join("\n")}`);
  }

  // Cold-call notes (latest call)
  if (options.coldCallNotes && options.coldCallNotes.trim()) {
    parts.push(`Latest cold-call notes:\n${options.coldCallNotes.trim()}`);
  }

  // Site weaknesses
  const weaknesses: string[] = [];
  if (Array.isArray(lead.scoring_reasons)) {
    for (const r of lead.scoring_reasons) {
      if (r?.impact === "negative" && r?.label) weaknesses.push(r.label);
    }
  }
  if (weaknesses.length) {
    parts.push(
      `Key site weaknesses identified:\n${weaknesses.map((w) => `- ${w}`).join("\n")}`,
    );
  }

  // Legacy free-form notes
  if (lead.notes?.trim()) {
    parts.push(`Additional notes:\n${lead.notes.trim()}`);
  }

  // Feature checklist (only if any chips selected)
  if (options.selectedFeatureChips && options.selectedFeatureChips.length > 0) {
    const block = renderFeaturesBlock(options.selectedFeatureChips);
    if (block) parts.push(block);
  }

  return parts.join("\n\n");
}

/**
 * Extract the notes portion from an operator-edited council prompt.
 *
 * Strategy (in order):
 *  1. If the edited text contains an "Additional notes:" section, return
 *     everything between that marker and the next auto-managed block (or the
 *     end of the text). This handles the common case where the operator
 *     edited text inside (or after) the notes block.
 *  2. If the edited text simply extends the base prompt (no notes), return
 *     whatever was appended after the auto-generated content — again
 *     stopping at the feature checklist block so chip selections never
 *     get persisted into the lead's saved notes.
 *  3. Otherwise — the edit was to generated sections we can't confidently
 *     isolate — return the existing notes unchanged as a safe no-op so that
 *     ambiguous edits never accidentally clear stored notes.
 */
export function extractOperatorNotes(lead: CouncilLead, editedText: string): string {
  const stripBlock = (s: string): string => {
    const blockIdx = s.indexOf(FEATURES_BLOCK_START);
    return blockIdx !== -1 ? s.slice(0, blockIdx) : s;
  };
  const notesMarker = "Additional notes:\n";
  const markerIdx = editedText.indexOf(notesMarker);
  if (markerIdx !== -1) {
    return stripBlock(editedText.slice(markerIdx + notesMarker.length)).trim();
  }

  const { id: _id, ...leadWithoutId } = lead;
  const baseText = buildCouncilPrefillText({ ...leadWithoutId, notes: null });
  if (editedText.startsWith(baseText)) {
    return stripBlock(editedText.slice(baseText.length)).trim();
  }

  return lead.notes ?? "";
}

/**
 * Strip protocol + trailing slash from a URL for compact display in tables.
 */
export function prettyHost(url: string | null | undefined): string {
  if (!url) return "";
  return url
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/+$/, "");
}
